import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Building2, Check, Clock, Loader2, Plus, RefreshCw, X, XCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import type { Claim, ClaimsDashboard, ClaimStatus, InsuranceProvider } from '../types/claims';
import { CLAIM_STATUS_COLORS, CLAIM_STATUS_LABELS } from '../types/claims';

interface AgeAnalysisBucket {
  total: number;
  count: number;
}

interface AgeAnalysisClaim extends Claim {
  daysOutstanding: number;
}

interface AgeAnalysisData {
  summary: Record<string, AgeAnalysisBucket>;
  buckets: Record<string, AgeAnalysisClaim[]>;
}

type Tab = 'claims' | 'dashboard' | 'age' | 'providers';

export function ClaimsPage() {
  const { get, post, patch, del } = useApi();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('claims');
  const [claims, setClaims] = useState<Claim[]>([]);
  const [dashboard, setDashboard] = useState<ClaimsDashboard | null>(null);
  const [ageData, setAgeData] = useState<AgeAnalysisData | null>(null);
  const [providers, setProviders] = useState<InsuranceProvider[]>([]);
  const providerMap = React.useMemo(() => new Map(providers.map(p => [p.id, p.name])), [providers]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modals
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [providerForm, setProviderForm] = useState({ name: '', code: '', claimsEmail: '', preAuthPhone: '', notes: '' });
  const [showStatusUpdate, setShowStatusUpdate] = useState<Claim | null>(null);
  const [statusForm, setStatusForm] = useState({ status: '' as ClaimStatus, amountPaid: '', rejectionReason: '', notes: '' });

  const fetchClaims = useCallback(async () => {
    const params = statusFilter ? `?status=${statusFilter}` : '';
    const result = await get<Claim[]>(`/claims${params}`);
    if (result.success && result.data) setClaims(result.data);
    else setError(result.error || 'Failed to load claims');
  }, [get, statusFilter]);

  const fetchDashboard = useCallback(async () => {
    const result = await get<ClaimsDashboard>('/claims/dashboard');
    if (result.success && result.data) setDashboard(result.data);
    else setError(result.error || 'Failed to load claims dashboard');
  }, [get]);

  const fetchAge = useCallback(async () => {
    const result = await get<AgeAnalysisData>('/claims/age-analysis');
    if (result.success && result.data) setAgeData(result.data);
    else setError(result.error || 'Failed to load age analysis');
  }, [get]);

  const fetchProviders = useCallback(async () => {
    const result = await get<InsuranceProvider[]>('/insurance-providers');
    if (result.success && result.data) setProviders(result.data);
    else setError(result.error || 'Failed to load insurance providers');
  }, [get]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchClaims(), fetchProviders(), fetchDashboard(), fetchAge()])
      .finally(() => setLoading(false));
  }, [fetchClaims, fetchProviders, fetchDashboard, fetchAge]);

  const handleAddProvider = useCallback(async () => {
    const result = await post('/insurance-providers', providerForm);
    if (result.success) {
      setShowAddProvider(false);
      setProviderForm({ name: '', code: '', claimsEmail: '', preAuthPhone: '', notes: '' });
      fetchProviders();
    } else setError(result.error || 'Failed to create provider');
  }, [post, providerForm, fetchProviders]);

  const handleUpdateStatus = useCallback(async () => {
    if (!showStatusUpdate) return;
    const body: any = { status: statusForm.status, notes: statusForm.notes };
    if (statusForm.status === 'paid' && statusForm.amountPaid) body.amountPaid = parseFloat(statusForm.amountPaid);
    if (statusForm.status === 'rejected') body.rejectionReason = statusForm.rejectionReason;

    const result = await patch(`/claims/${showStatusUpdate.id}/status`, body);
    if (result.success) {
      setShowStatusUpdate(null);
      fetchClaims();
      fetchDashboard();
      fetchAge();
    } else setError(result.error || 'Failed to update');
  }, [showStatusUpdate, statusForm, patch, fetchClaims, fetchDashboard, fetchAge]);

  const handleResubmit = useCallback(async (claimId: string) => {
    const result = await post(`/claims/${claimId}/resubmit`, {});
    if (result.success) { fetchClaims(); fetchDashboard(); fetchAge(); }
    else setError(result.error || 'Failed to resubmit claim');
  }, [post, fetchClaims, fetchDashboard, fetchAge]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  const TABS: { id: Tab; label: string }[] = [
    { id: 'claims', label: 'Claims' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'age', label: 'Age Analysis' },
    { id: 'providers', label: 'Providers' },
  ];

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Building2 className="h-5 w-5" /> Claims</h1>
        <div className="flex gap-2">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${tab === t.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {/* Claims List */}
      {tab === 'claims' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            {['', 'submitted', 'processing', 'paid', 'rejected', 'queried'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs rounded-full border font-medium ${statusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600'}`}>
                {s ? CLAIM_STATUS_LABELS[s as ClaimStatus] : 'All'}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {claims.length === 0 ? (
              <p className="p-8 text-center text-gray-500">No claims found.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Claimed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {claims.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <button onClick={() => navigate(`/billing/${c.invoiceId}`)} className="text-blue-600 hover:underline">{c.invoiceNumber}</button>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{c.patientName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{c.providerName || providerMap.get(c.providerId) || c.providerId || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">R {c.amountClaimed.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${CLAIM_STATUS_COLORS[c.status]}`}>
                          {CLAIM_STATUS_LABELS[c.status]}
                          {c.resubmissionCount > 0 && ` (${c.resubmissionCount}x)`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{c.referenceNumber || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{c.submittedAt?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => { setShowStatusUpdate(c); setStatusForm({ status: '' as ClaimStatus, amountPaid: '', rejectionReason: '', notes: '' }); }}
                            className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded">Update</button>
                          {(c.status === 'rejected' || c.status === 'queried') && (
                            <button onClick={() => handleResubmit(c.id)}
                              className="px-2 py-1 text-xs text-orange-600 hover:bg-orange-50 rounded flex items-center gap-1">
                              <RefreshCw className="h-3 w-3" /> Resubmit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Dashboard */}
      {tab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'Total Claimed', value: dashboard.totals.claimed, color: 'text-blue-700' },
              { label: 'Total Paid', value: dashboard.totals.paid, color: 'text-green-700' },
              { label: 'Outstanding', value: dashboard.totals.outstanding, color: 'text-orange-600' },
              { label: 'Rejected', value: dashboard.totals.rejected, color: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-lg shadow-sm border p-4">
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className={`text-xl font-bold ${s.color}`}>R {s.value.toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Claims</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Claimed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rejection %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dashboard.providers.map(p => (
                  <tr key={p.providerId}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.providerName || p.providerId || 'Unknown'}</td>
                    <td className="px-4 py-3 text-sm text-right">{p.count}</td>
                    <td className="px-4 py-3 text-sm text-right">R {p.claimed.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-green-700">R {p.paid.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-orange-600">R {p.outstanding.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={p.rejectionRate > 20 ? 'text-red-600 font-bold' : ''}>{p.rejectionRate}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Age Analysis */}
      {tab === 'age' && ageData && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'Current (<30d)', key: 'current', color: 'text-green-700 bg-green-50 border-green-200' },
              { label: '30-60 days', key: '30', color: 'text-yellow-700 bg-yellow-50 border-yellow-200' },
              { label: '60-90 days', key: '60', color: 'text-orange-700 bg-orange-50 border-orange-200' },
              { label: '90+ days', key: '90_plus', color: 'text-red-700 bg-red-50 border-red-200' },
            ].map(b => (
              <div key={b.key} className={`rounded-lg border p-4 ${b.color}`}>
                <p className="text-xs font-medium">{b.label}</p>
                <p className="text-lg font-bold">R {ageData.summary[b.key]?.total.toFixed(2) || '0.00'}</p>
                <p className="text-xs">{ageData.summary[b.key]?.count || 0} claims</p>
              </div>
            ))}
          </div>
          {Object.entries(ageData.buckets).map(([key, items]) => (
            items.length > 0 && (
              <div key={key} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b">
                  <h3 className="text-sm font-semibold">{key === 'current' ? 'Current' : key === '90_plus' ? '90+ days' : `${key}-${parseInt(key) + 30} days`}</h3>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs text-gray-500">Patient</th>
                      <th className="px-4 py-2 text-left text-xs text-gray-500">Provider</th>
                      <th className="px-4 py-2 text-left text-xs text-gray-500">Invoice</th>
                      <th className="px-4 py-2 text-right text-xs text-gray-500">Amount</th>
                      <th className="px-4 py-2 text-right text-xs text-gray-500">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((c) => (
                      <tr key={c.id}>
                        <td className="px-4 py-2 text-sm">{c.patientName}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{c.providerName}</td>
                        <td className="px-4 py-2 text-sm">
                          <button onClick={() => navigate(`/billing/${c.invoiceId}`)} className="text-blue-600 hover:underline">{c.invoiceNumber}</button>
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-medium">R {c.amountClaimed.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-right font-bold">{c.daysOutstanding}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ))}
        </div>
      )}

      {/* Providers */}
      {tab === 'providers' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowAddProvider(true)}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5">
              <Plus className="h-4 w-4" /> Add Provider
            </button>
          </div>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {providers.length === 0 ? (
              <p className="p-8 text-center text-gray-500">No insurance providers registered.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claims Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pre-Auth Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {providers.map(p => (
                    <tr key={p.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{p.code || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{p.claimsEmail || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{p.preAuthPhone || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{p.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Add Provider Modal */}
      {showAddProvider && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Add Insurance Provider</h2>
              <button onClick={() => setShowAddProvider(false)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input value={providerForm.name} onChange={e => setProviderForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input value={providerForm.code} onChange={e => setProviderForm(f => ({ ...f, code: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Claims Email</label>
                <input value={providerForm.claimsEmail} onChange={e => setProviderForm(f => ({ ...f, claimsEmail: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Pre-Auth Phone</label>
                <input value={providerForm.preAuthPhone} onChange={e => setProviderForm(f => ({ ...f, preAuthPhone: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={providerForm.notes} onChange={e => setProviderForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border rounded" rows={2} /></div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowAddProvider(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button onClick={handleAddProvider} disabled={!providerForm.name} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusUpdate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Update Claim Status</h2>
              <button onClick={() => setShowStatusUpdate(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {showStatusUpdate.patientName} — R {showStatusUpdate.amountClaimed.toFixed(2)} via {showStatusUpdate.providerName || providerMap.get(showStatusUpdate.providerId) || showStatusUpdate.providerId || 'Unknown'}
            </p>
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                <select value={statusForm.status} onChange={e => setStatusForm(f => ({ ...f, status: e.target.value as ClaimStatus }))} className="w-full px-3 py-2 border rounded">
                  <option value="">Select...</option>
                  <option value="processing">Processing</option>
                  <option value="paid">Paid</option>
                  <option value="rejected">Rejected</option>
                  <option value="queried">Queried</option>
                </select></div>
              {statusForm.status === 'paid' && (
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
                  <input type="number" step="0.01" value={statusForm.amountPaid} onChange={e => setStatusForm(f => ({ ...f, amountPaid: e.target.value }))}
                    placeholder={showStatusUpdate.amountClaimed.toFixed(2)} className="w-full px-3 py-2 border rounded" /></div>
              )}
              {statusForm.status === 'rejected' && (
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
                  <input value={statusForm.rejectionReason} onChange={e => setStatusForm(f => ({ ...f, rejectionReason: e.target.value }))} className="w-full px-3 py-2 border rounded" /></div>
              )}
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={statusForm.notes} onChange={e => setStatusForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border rounded" rows={2} /></div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowStatusUpdate(null)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button onClick={handleUpdateStatus} disabled={!statusForm.status} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Update</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
